import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/shared";

import Landing from "@/pages/Landing";
import Join from "@/pages/Join";
import Auth from "@/pages/Auth";

import MasqLayout from "@/masq/MasqLayout";
import Home from "@/masq/Home";
import Passport from "@/masq/Passport";
import RoadDay from "@/masq/RoadDay";
import Crew from "@/masq/Crew";
import Updates from "@/masq/Updates";
import Help from "@/masq/Help";
import SafePoints from "@/masq/SafePoints";
import GetHome from "@/masq/GetHome";
import Wallet from "@/masq/Wallet";
import Impact from "@/masq/Impact";
import More from "@/masq/More";

import LeaderLayout from "@/leader/LeaderLayout";
import Dashboard from "@/leader/Dashboard";
import Participants from "@/leader/Participants";
import Communications from "@/leader/Communications";
import HelpRequests from "@/leader/HelpRequests";
import SafePointsAdmin from "@/leader/SafePointsAdmin";
import Reports from "@/leader/Reports";

import BrandPortal from "@/brand/BrandPortal";

import AdminLayout from "@/admin/AdminLayout";
import AdminOverview from "@/admin/Overview";
import AdminBands from "@/admin/Bands";
import AdminParticipants from "@/admin/AdminParticipants";
import Partnerships from "@/admin/Partnerships";

function Guard({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="cs-shell grid place-items-center"><Spinner label="Loading…" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="grid place-items-center min-h-screen"><Spinner /></div>;
  if (user && user.role === "band_leader") return <Navigate to="/leader" replace />;
  if (user && user.role === "brand_partner") return <Navigate to="/brand" replace />;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  if (user && user.role === "masquerader") return <Navigate to="/app" replace />;
  return <Landing />;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname.split("/").slice(0, 3).join("/")}>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/join" element={<Join />} />
        <Route path="/login" element={<Auth mode="login" />} />
        <Route path="/register" element={<Auth mode="register" />} />

        <Route path="/app" element={<Guard roles={["masquerader"]}><MasqLayout /></Guard>}>
          <Route index element={<Home />} />
          <Route path="passport" element={<Passport />} />
          <Route path="road-day" element={<RoadDay />} />
          <Route path="crew" element={<Crew />} />
          <Route path="updates" element={<Updates />} />
          <Route path="help" element={<Help />} />
          <Route path="safe-points" element={<SafePoints />} />
          <Route path="get-home" element={<GetHome />} />
          <Route path="wallet" element={<Wallet />} />
          <Route path="impact" element={<Impact />} />
          <Route path="more" element={<More />} />
        </Route>

        <Route path="/leader" element={<Guard roles={["band_leader"]}><LeaderLayout /></Guard>}>
          <Route index element={<Dashboard />} />
          <Route path="participants" element={<Participants />} />
          <Route path="communications" element={<Communications />} />
          <Route path="help" element={<HelpRequests />} />
          <Route path="safe-points" element={<SafePointsAdmin />} />
          <Route path="reports" element={<Reports />} />
        </Route>

        <Route path="/brand" element={<Guard roles={["brand_partner"]}><BrandPortal /></Guard>} />

        <Route path="/admin" element={<Guard roles={["admin"]}><AdminLayout /></Guard>}>
          <Route index element={<AdminOverview />} />
          <Route path="bands" element={<AdminBands />} />
          <Route path="participants" element={<AdminParticipants />} />
          <Route path="partnerships" element={<Partnerships />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AnimatedRoutes />
        <Toaster theme="dark" position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
